/**
 * InvalidPowersModal Component
 * Modal shown during report submission when power status validation finds powers
 * that cannot be submitted due to their current bond status (e.g., already executed,
 * transferred, discharged, etc.). Allows user to remove invalid powers and continue,
 * go back, or print the list for reference.
 */

import { ExclamationTriangleIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner } from '../../../shared/OathUI';
import { STATUS_LABELS } from '../utils/constants';

/**
 * Returns Tailwind classes for a status badge based on the power's current status code.
 * @param {string} status - Single-letter or short status code (E, T, D, F, J, V, UT, MISSING)
 * @returns {string} Tailwind CSS classes for the badge
 */
const getStatusBadgeClasses = (status) => {
  switch (status) {
    case 'E':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'T':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    case 'D':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'F':
    case 'J':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const InvalidPowersModal = ({
  isOpen,
  onClose,
  validationData,
  reportId,
  onRemoveInvalid,
  isRemoving
}) => {
  if (!isOpen || !validationData) return null;

  const { invalidPowerCount, totalPowerCount, invalidPowers } = validationData;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const rows = validationData.invalidPowers.map(p => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; font-family: monospace;">${p.powerNumber || 'N/A'}</td>
        <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${[p.defendantFirstName, p.defendantLastName].filter(Boolean).join(' ') || '-'}</td>
        <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${STATUS_LABELS[p.currentStatus] || p.currentStatus || 'Unknown'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html>
        <head><title>Invalid Powers - Report ${reportId}</title></head>
        <body style="font-family: Arial, sans-serif; padding: 20px;">
          <h2>Power Status Check - Report ${reportId}</h2>
          <p style="color: #dc2626; font-size: 18px; font-weight: bold;">
            ${validationData.invalidPowerCount} out of ${validationData.totalPowerCount} power(s) cannot be submitted
          </p>
          <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
            <thead>
              <tr style="background: #f3f4f6;">
                <th style="padding: 8px; text-align: left; border-bottom: 2px solid #d1d5db;">Power #</th>
                <th style="padding: 8px; text-align: left; border-bottom: 2px solid #d1d5db;">Defendant</th>
                <th style="padding: 8px; text-align: left; border-bottom: 2px solid #d1d5db;">Current Status</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <p style="margin-top: 16px; color: #6b7280; font-size: 12px;">Printed on ${new Date().toLocaleString()}</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={isRemoving ? undefined : onClose}
      title="Power Status Check"
      size="lg"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={isRemoving}
            className="w-full sm:w-auto"
          >
            Go Back
          </OathButton>
          <OathButton
            variant="outline"
            onClick={handlePrint}
            disabled={isRemoving}
            icon={PrinterIcon}
            className="w-full sm:w-auto"
          >
            Print
          </OathButton>
          <OathButton
            onClick={onRemoveInvalid}
            disabled={isRemoving}
            className="w-full sm:w-auto"
          >
            {isRemoving ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Removing...
              </span>
            ) : (
              `Remove ${validationData?.invalidPowerCount || 0} & Continue`
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Warning block */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <p className="text-2xl font-bold text-red-700 dark:text-red-400">
            {invalidPowerCount} out of {totalPowerCount}
          </p>
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
            power(s) cannot be submitted due to current bond status
          </p>
        </div>

        {/* Desktop table - hidden on mobile, shown md+ */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Power #</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Defendant</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Current Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {invalidPowers.map((power) => {
                const defendant = [power.defendantFirstName, power.defendantLastName].filter(Boolean).join(' ') || '-';

                return (
                  <tr key={power.powerReportPowerId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-3">
                      <span className="font-mono font-medium text-gray-900 dark:text-white">
                        {power.powerNumber || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-700 dark:text-gray-300">
                      {defendant}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(power.currentStatus)}`}>
                        {STATUS_LABELS[power.currentStatus] || power.currentStatus}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card list - shown on small screens, hidden md+ */}
        <div className="md:hidden space-y-2">
          {invalidPowers.map((power) => {
            const defendant = [power.defendantFirstName, power.defendantLastName].filter(Boolean).join(' ') || '-';

            return (
              <div key={power.powerReportPowerId} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Power #</span>
                  <span className="font-mono font-medium text-gray-900 dark:text-white text-sm">
                    {power.powerNumber || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Defendant</span>
                  <span className="text-gray-700 dark:text-gray-300 text-sm">
                    {defendant}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(power.currentStatus)}`}>
                    {STATUS_LABELS[power.currentStatus] || power.currentStatus}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </OathModal>
  );
};

export default InvalidPowersModal;
